// Funciones de validación comunes para formularios

// Validar formato de email
export const validarEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validar formato de teléfono (estricto, compatibilidad con formularios legacy)
export const validarTelefono = (telefono: string): boolean => {
  const telefonoRegex = /^\+?[0-9]{10,15}$/;
  return telefonoRegex.test(telefono.replace(/\D/g, ""));
};

/** Teléfono humano: tras quitar símbolos, entre 8 y 15 dígitos. Vacío = válido (opcional). */
export const validarTelefonoHumano = (telefono: string): boolean => {
  if (!telefono?.trim()) return true;
  const digits = telefono.replace(/\D/g, "");
  return digits.length >= 8 && digits.length <= 15;
};

/** Prefijos de tipo CUIT aceptados (incl. jurídicos y monotributo social 34). Alineado con validación fiscal */
export const CUIT_PREFIJOS_VALIDOS = [20, 23, 27, 30, 33, 34] as const;

const esPrefijoCuitValido = (tipo: number): boolean =>
  (CUIT_PREFIJOS_VALIDOS as readonly number[]).includes(tipo);

/**
 * Documento extranjero, reconocido por cómo se escribe (no hay que elegir el
 * tipo): con letras, como un pasaporte ("AAB123456"), o con el verificador
 * después de un guion, como un RUT chileno ("12.715.473-2"). Lo que empieza
 * con "DNI" o "CUIT" es un documento argentino con el prefijo escrito. Mismo
 * criterio que el backend (validation_service.tipo_documento).
 */
export const esDocumentoExtranjero = (valor: string): boolean => {
  const texto = (valor || "").trim();
  if (/^(dni|cui[tl])/i.test(texto)) return false;
  if (/^\d{1,3}(\.?\d{3}){1,3}-[\dkK]$/.test(texto)) return true;
  return (
    /^[A-Za-z0-9]{6,15}$/.test(texto) &&
    /[A-Za-z]/.test(texto) &&
    texto.replace(/\D/g, "").length >= 3
  );
};

/** Ayuda bajo el campo cuando el documento es extranjero. */
export const AYUDA_DOCUMENTO_EXTRANJERO = "Documento extranjero: no se verifica con ARCA.";

/** Error de un documento de persona que no es DNI ni extranjero. */
export const ERROR_DOCUMENTO_PERSONA =
  "El DNI debe tener 7 u 8 dígitos. Si es extranjero, escribilo como figura en su documento.";

/** Error de un documento que puede ser de persona o de empresa. */
export const ERROR_DOCUMENTO =
  "Ingresá un DNI (7 u 8 dígitos) o un CUIT (11). Si es extranjero, escribilo como figura en su documento.";

// Validar formato de DNI/CUIT (o documento extranjero)
export const validarDNI = (dni: string): boolean => {
  if (!dni || typeof dni !== "string") return false;
  if (esDocumentoExtranjero(dni)) return true;

  // Limpiar: solo números
  const numeros = dni.replace(/\D/g, "");

  // Validar longitud
  if (numeros.length < 7 || numeros.length > 11) return false;

  // Si es DNI (7 u 8 dígitos: los de 7 son de personas mayores)
  if (numeros.length === 7 || numeros.length === 8) return true;

  // Si es CUIT (11 dígitos)
  if (numeros.length === 11) {
    const tipo = parseInt(numeros.substring(0, 2), 10);
    if (!esPrefijoCuitValido(tipo)) return false;

    return /^\d{11}$/.test(numeros);
  }

  return false;
};

/** Documento de una persona (directo): DNI de 7 u 8 dígitos o documento extranjero. */
export const validarDNIPersonaDirecto = (dni: string): boolean => {
  if (!dni || typeof dni !== "string") return false;
  const numeros = dni.replace(/\D/g, "");
  return /^\d{7,8}$/.test(numeros) || esDocumentoExtranjero(dni);
};

/** Código Booking/Airbnb: obligatorio; letras, números, espacios y guiones habituales. */
export const validarCodigoExternoCliente = (code: string): boolean => {
  const t = (code || "").trim();
  if (!t) return false;
  return /^[A-Za-zÁÉÍÓÚÜáéíóúüÑñ0-9\s-]+$/.test(t);
};

// Validar CUIT específicamente (con dígito verificador)
export const validarCUIT = (cuit: string): boolean => {
  if (!cuit || typeof cuit !== "string") return false;

  // Limpiar: solo números
  const numeros = cuit.replace(/\D/g, "");

  if (numeros.length !== 11) return false;

  const tipo = parseInt(numeros.substring(0, 2), 10);
  if (!esPrefijoCuitValido(tipo)) return false;

  // Validar dígito verificador
  const multiplicadores = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let suma = 0;

  for (let i = 0; i < 10; i++) {
    suma += parseInt(numeros[i]) * multiplicadores[i];
  }

  const resto = suma % 11;
  const digitoVerificador = resto === 0 ? 0 : 11 - resto;

  return parseInt(numeros[10]) === digitoVerificador;
};

// Validar edad
export const validarEdad = (edad: string): boolean => {
  const edadNum = parseInt(edad);
  return !isNaN(edadNum) && edadNum >= 0 && edadNum <= 120;
};

// Validar campos obligatorios
export const validarCampoRequerido = (valor: string): boolean => {
  return valor.trim().length > 0;
};

// Validar fechas
export const validarFechas = (
  entrada: Date | null,
  salida: Date | null
): boolean => {
  if (!entrada || !salida) return false;

  // Validar que la fecha de salida sea posterior a la de entrada
  if (salida <= entrada) return false;

  // Validar mínimo 1 noche (diferencia de al menos 1 día)
  const daysDifference = Math.ceil(
    (salida.getTime() - entrada.getTime()) / (1000 * 3600 * 24)
  );
  return daysDifference >= 1;
};

// Validar fechas con mensaje de error específico
export const validarFechasConMensaje = (
  entrada: Date | null,
  salida: Date | null
): string | null => {
  if (!entrada || !salida)
    return "Las fechas de entrada y salida son obligatorias";

  // Validar que la fecha de salida sea posterior a la de entrada
  if (salida <= entrada) {
    return "La fecha de salida debe ser posterior a la fecha de entrada";
  }

  // Validar mínimo 1 noche (diferencia de al menos 1 día)
  const daysDifference = Math.ceil(
    (salida.getTime() - entrada.getTime()) / (1000 * 3600 * 24)
  );
  if (daysDifference < 1) {
    return "La reserva debe ser de al menos 1 noche. Las fechas de entrada y salida no pueden ser iguales";
  }

  return null; // Sin errores
};

// Validar valor numérico positivo
export const validarValorPositivo = (valor: string): boolean => {
  const num = parseFloat(valor);
  return !isNaN(num) && num >= 0;
};

// Validar cantidad de huéspedes
export const validarCantidadHuespedes = (cantidad: string): boolean => {
  const num = parseInt(cantidad);
  return !isNaN(num) && num > 0;
};

// Validar seña (solo verificar que no sea negativa)
export const validarSeña = (pago: number, _total: number): string | null => {
  if (pago < 0) {
    return "La seña no puede ser negativa";
  }
  return null;
};
